import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { useFood } from "../../context/FoodContext";
import ParseMenu from "../ParseMenu/ParseMenu";
import "./AddFoodItem.css";

const AddFoodItem = ({
  editMode,
  restaurantData,
  updateRestaurantData,
  nextStep, 
  prevStep, 
}) => {
  const { foodID } = useParams();
  const { user } = useContext(AuthContext);
  const {
    createFoodItem,
    updateFoodItem,
    fetchAllFoodItems,
    fetchRestaurants,
  } = useFood();

  // Food item fields (restaurantID and userID are removed)
  const [foodData, setFoodData] = useState({
    name: "",
    category: "",
    ingredients: "",
  });

  // Available size options
  const SIZE_OPTIONS = ["Individual", "Small", "Large"];

  // Which sizes are selected
  const [selectedSizes, setSelectedSizes] = useState([]);

  // For each size, store price and serves values
  const [sizeData, setSizeData] = useState({
    Individual: { price: "", serves: "" },
    Small: { price: "", serves: "" },
    Large: { price: "", serves: "" },
  });

  const [restaurants, setRestaurants] = useState([]);
  const [isParseModalOpen, setIsParseModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch user’s restaurants for ParseMenu (if needed)
  useEffect(() => {
    if (user && user.userID) {
      const fetchRestaurantsData = async () => {
        const restaurantNames = await fetchRestaurants(user.userID);
        setRestaurants(restaurantNames);
      };
      fetchRestaurantsData();
    }
  }, [user, fetchRestaurants]);

  // If in edit mode, load existing food data (if applicable)
  useEffect(() => {
    if (editMode && foodID) {
      const fetchFoodData = async () => {
        try {
          const response = await axios.get(`/api/food-items/${foodID}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          if (response.status === 200) {
            const existing = response.data;
            setFoodData({
              name: existing.name || "",
              category: existing.category || "",
              ingredients: existing.ingredients || "",
            });

            // Parse price string if it exists (e.g. "Individual $10 | Small $49")
            if (existing.price) {
              const parts = existing.price.split("|").map((p) => p.trim());
              const newSelectedSizes = [];
              const newSizeData = { ...sizeData };

              parts.forEach((part) => {
                const [sizeLabel, priceWithDollar] = part.split("$");
                const labelTrimmed = sizeLabel.trim();
                const priceValue = priceWithDollar ? priceWithDollar.trim() : "";
                if (SIZE_OPTIONS.includes(labelTrimmed)) {
                  newSelectedSizes.push(labelTrimmed);
                  newSizeData[labelTrimmed].price = priceValue;
                }
              });
              setSelectedSizes(newSelectedSizes);
              setSizeData(newSizeData);
            }

            // Parse servingSize object if it exists
            if (existing.servingSize) {
              const newSizeData = { ...sizeData };
              Object.keys(existing.servingSize).forEach((sz) => {
                if (SIZE_OPTIONS.includes(sz)) {
                  const numeric = existing.servingSize[sz].replace(/[^0-9]/g, "");
                  newSizeData[sz].serves = numeric;
                }
              });
              setSizeData(newSizeData);
            }
          } else {
            throw new Error("Failed to fetch food data");
          }
        } catch (error) {
          alert("Failed to fetch food data. Please try again.");
        }
      };
      fetchFoodData();
    }
  }, [editMode, foodID, user, sizeData]);

  // Handle basic input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFoodData((prev) => ({ ...prev, [name]: value }));
  };

  // Toggle size checkbox selection
  const handleSizeCheckboxChange = (sizeOption) => {
    setSelectedSizes((prev) => {
      if (prev.includes(sizeOption)) {
        return prev.filter((sz) => sz !== sizeOption);
      } else {
        return [...prev, sizeOption];
      }
    });
  };

  // Handle changes for price and serves inputs for each size
  const handleSizeDataChange = (sizeOption, field, value) => {
    setSizeData((prev) => ({
      ...prev,
      [sizeOption]: { ...prev[sizeOption], [field]: value },
    }));
  };

  // Handle form submission, update the parent's foodItems, and proceed to next step
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Build final price string (e.g. "Individual $10 | Small $49")
    const finalPriceParts = [];
    selectedSizes.forEach((sz) => {
      const priceVal = sizeData[sz].price;
      if (priceVal) {
        finalPriceParts.push(`${sz} $${priceVal}`);
      }
    });
    const finalPrice = finalPriceParts.join(" | ");

    // Build servingSize object (e.g. { Individual: "Serves 1 person", Small: "Serves 5 people" })
    const servingSizeObj = {};
    selectedSizes.forEach((sz) => {
      const servesVal = sizeData[sz].serves;
      if (servesVal) {
        const plural = Number(servesVal) > 1 ? "s" : "";
        servingSizeObj[sz] = `Serves ${servesVal} person${plural}`;
      }
    });

    // Food item payload without restaurantID and userID
    const foodItemPayload = {
      name: foodData.name,
      price: finalPrice,
      ingredients: foodData.ingredients,
      servingSize: servingSizeObj,
    };

    // Update the parent's foodItems in the structure:
    // { foodCategory: { <category>: [ foodItemPayload, ... ] } }
    let updatedFoodItems = {};
    if (restaurantData.foodItems && restaurantData.foodItems.foodCategory) {
      updatedFoodItems = { ...restaurantData.foodItems };
    } else {
      updatedFoodItems.foodCategory = {};
    }
    const category = foodData.category;
    if (!updatedFoodItems.foodCategory[category]) {
      updatedFoodItems.foodCategory[category] = [];
    }
    updatedFoodItems.foodCategory[category].push(foodItemPayload);
    updateRestaurantData({ foodItems: updatedFoodItems });

    // Clear form fields for next entry
    setFoodData({ name: "", category: "", ingredients: "" });
    setSelectedSizes([]);
    setSizeData({
      Individual: { price: "", serves: "" },
      Small: { price: "", serves: "" },
      Large: { price: "", serves: "" },
    });

    setLoading(false);
    // Proceed to the next component/step
    if (nextStep) {
      nextStep();
    }
  };

  const handleParsedMenuSubmit = async (parsedArray, restaurantID) => {
    if (!Array.isArray(parsedArray) || parsedArray.length === 0) {
      alert("Parsed menu data is empty or invalid.");
      return;
    }
  
    const requiredFields = ["name", "category", "price", "ingredients", "size", "serves"];
    const missingFieldsLog = [];
  
    // Validate items
    parsedArray.forEach((item, index) => {
      const missingFields = requiredFields.filter(field => !item[field] || item[field].toString().trim() === "");
      if (missingFields.length > 0) {
        missingFieldsLog.push(`Item #${index + 1} missing: ${missingFields.join(", ")}`);
      }
    });
  
    if (missingFieldsLog.length > 0) {
      alert(`Validation failed!\n${missingFieldsLog.join("\n")}`);
      return;
    }
  
    // Transform to desired structure
    const foodCategory = {};
  
    parsedArray.forEach(item => {
      const { name, category, price, ingredients, size, serves } = item;
  
      if (!foodCategory[category]) foodCategory[category] = [];
  
      // Build price and servingSize structure according to the standard
      const priceFormatted = `${size} $${price}`;
      const servingSizeFormatted = { [size]: `Serves ${serves} ${serves > 1 ? 'people' : 'person'}` };
  
      foodCategory[category].push({
        name,
        price: priceFormatted,
        ingredients,
        servingSize: servingSizeFormatted,
      });
    });
  
    const updatedFoodItems = restaurantData.foodItems?.foodCategory
      ? { ...restaurantData.foodItems }
      : { foodCategory: {} };
  
    Object.entries(foodCategory).forEach(([category, items]) => {
      if (!updatedFoodItems.foodCategory[category]) {
        updatedFoodItems.foodCategory[category] = [];
      }
      updatedFoodItems.foodCategory[category].push(...items);
    });
  
    updateRestaurantData({ foodItems: updatedFoodItems });
    alert("Menu items added successfully!");
    if (nextStep) nextStep();
  };  
  
  return (
    <div className="add-food-item-container">
      <h2 className="form-title">
        {editMode ? "Edit Food Item" : "Add New Food Item"}
      </h2>

      <div className="upload-menu">
        <button
          className="upload-menu-btn"
          onClick={() => setIsParseModalOpen(true)}
        >
          Upload Menu
        </button>
        <ParseMenu
          isOpen={isParseModalOpen}
          onClose={() => setIsParseModalOpen(false)}
          restaurantOptions={restaurants}
          onParsedMenuSubmit={handleParsedMenuSubmit}
        />
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={foodData.name}
              onChange={handleChange}
              placeholder="Food Name"
              required
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            <input
              type="text"
              name="category"
              value={foodData.category}
              onChange={handleChange}
              placeholder="e.g. Salads, Sandwiches, Entrees"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label>Ingredients</label>
          <textarea
            name="ingredients"
            value={foodData.ingredients}
            onChange={handleChange}
            placeholder="Ingredients"
            required
          />
        </div>

        {/* Checkboxes for size selection */}
        <div className="form-group">
          <label>Available Sizes</label>
          <div className="checkbox-group">
            {SIZE_OPTIONS.map((sizeOption) => (
              <label key={sizeOption}>
                <input
                  type="checkbox"
                  value={sizeOption}
                  checked={selectedSizes.includes(sizeOption)}
                  onChange={() => handleSizeCheckboxChange(sizeOption)}
                />
                {sizeOption}
              </label>
            ))}
          </div>
        </div>

        {/* For each selected size, display Price & Serves fields */}
        {selectedSizes.map((sz) => (
          <div key={sz} className="size-row">
            <h3>{sz}</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Price ($)</label>
                <input
                  type="number"
                  min="0"
                  value={sizeData[sz].price}
                  onChange={(e) =>
                    handleSizeDataChange(sz, "price", e.target.value)
                  }
                  placeholder="e.g. 10"
                />
              </div>
              <div className="form-group">
                <label>Serves</label>
                <input
                  type="number"
                  min="1"
                  value={sizeData[sz].serves}
                  onChange={(e) =>
                    handleSizeDataChange(sz, "serves", e.target.value)
                  }
                  placeholder="e.g. 1"
                />
              </div>
            </div>
          </div>
        ))}

        <div className="navigation-buttons">
          {prevStep && (
            <button type="button" onClick={prevStep} className="prev-btn">
              Previous
            </button>
          )}
          <button type="submit" className="next-btn" disabled={loading}>
            {loading ? "Saving..." : "Add & Next"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddFoodItem;
